import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Spinner,
  UncontrolledAccordion,
  UncontrolledDropdown,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  FormGroup,
  FormFeedback,
} from "reactstrap";
import { Pill } from "./style";
import React, { useEffect, useState } from "react";
import { CustomizationSpinnerContainer, Notification } from "../common/style";
import {
  getOrderInfo,
  getSignedToken,
  getMaterialItems,
  getFactoryMaterials,
  createMaterialItem,
  updateMaterialItem,
  deleteMaterialItem,
} from "../project/service";
import { useOutletContext, useParams } from "react-router-dom";
import { generateRequestUri } from "../common/service";
import ConfirmationModal from "../project/ConfirmationModal";

const NewOrder = ({
  projectSummary: projectSummaryFromProps,
  setCurrentTab,
  access,
}) => {
  const outletContext = useOutletContext() || {};
  const projectSummary =
    outletContext.projectSummary || projectSummaryFromProps;
  const refreshProjectSummary = outletContext.refreshProjectSummary;
  const isAdmin = outletContext.isAdmin || false;
  const [order, setOrder] = useState({});
  const [addOns, setAddOns] = useState({});
  const [materialItems, setMaterialItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("upgrades");
  const [addEditModalOpen, setAddEditModalOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [currentMaterialItem, setCurrentMaterialItem] = useState({
    id: null,
    material_id: "",
    price: "",
    quantity: "",
  });
  const params = useParams();
  const projectId = params.projectId;
  const resourceMapper = {
    order_summary: "download_order_summary",
    order_detailed_summary: "download_order_detailed",
  };

  useEffect(() => {
    if (!access.view_price_detail) setActiveTab("room");
  }, []);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const loadOrder = () => {
    setIsLoading(true);

    Promise.all([
      getOrderInfo({ projectId }),
      getMaterialItems({ projectId }).catch(() => []),
    ])
      .then(([orderResponse, materialItemsResponse]) => {
        setOrder(orderResponse);
        setAddOns(orderResponse.add_ons || []);
        setMaterialItems(materialItemsResponse || []);
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
      })
      .finally(() => setIsLoading(false));
  };

  const loadMaterials = () => {
    getFactoryMaterials({ projectId, includeArchived: true })
      .then((response) => {
        const materialsList = Array.isArray(response) ? response : (response?.materials || response?.data || []);
        setMaterials(materialsList);
      })
      .catch((error) => {
        console.error("Error fetching materials:", error);
        setIsNotificationVisible(true);
        setNotification({
          type: "error",
          message: error.error || "Error loading materials",
        });
        setMaterials([]);
      });
  };

  useEffect(() => {
    loadOrder();
  }, [projectSummary.is_price_stale]);

  const toggleAddEditModal = (materialItem = null) => {
    if (materialItem) {
      setCurrentMaterialItem({
        id: materialItem.id,
        material_id: materialItem.material_id || "",
        price: materialItem.price || "",
        quantity: materialItem.quantity || "",
      });
    } else {
      setCurrentMaterialItem({
        id: null,
        material_id: "",
        price: "",
        quantity: "",
      });
    }
    
    if (!addEditModalOpen) {
      loadMaterials();
    }
    
    setAddEditModalOpen(!addEditModalOpen);
  };

  const handleSaveMaterialItem = () => {
    const materialId = parseInt(currentMaterialItem.material_id);
    const price = parseFloat(currentMaterialItem.price);
    const quantity = parseInt(currentMaterialItem.quantity);

    if (!materialId || !price || !quantity || quantity <= 0 || price < 0) {
      return;
    }

    setIsLoading(true);
    const handler = currentMaterialItem.id
      ? updateMaterialItem
      : createMaterialItem;

    handler({
      projectId,
      id: currentMaterialItem.id,
      materialId,
      price,
      quantity,
    })
      .then(() => {
        setAddEditModalOpen(false);
        loadOrder();
        if (refreshProjectSummary) {
          refreshProjectSummary();
        }
        setIsNotificationVisible(true);
        setNotification({
          type: "success",
          message: currentMaterialItem.id
            ? "Material item updated successfully"
            : "Material item created successfully",
        });
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({
          type: "error",
          message: error.error || "Error saving material item",
        });
      })
      .finally(() => setIsLoading(false));
  };

  const toggleDeleteConfirmation = (item = null) => {
    setItemToDelete(item);
    setDeleteConfirmationOpen(!deleteConfirmationOpen);
  };

  const handleDeleteMaterialItem = () => {
    if (itemToDelete) {
      setIsLoading(true);
      deleteMaterialItem({
        projectId,
        id: itemToDelete.id,
      })
      .then(() => {
        setDeleteConfirmationOpen(false);
        setItemToDelete(null);
        loadOrder();
        if (refreshProjectSummary) {
          refreshProjectSummary();
        }
        setIsNotificationVisible(true);
        setNotification({
          type: "success",
          message: "Material item deleted successfully",
        });
      })
        .catch((error) => {
          setIsNotificationVisible(true);
          setNotification({
            type: "error",
            message: error.error || "Error deleting material item",
          });
        })
        .finally(() => setIsLoading(false));
    }
  };

  const downloadOrderInfo = (resource) => {
    setIsLoading(true);

    getSignedToken({ projectId, resource })
      .then(({ token }) =>
        window.open(
          generateRequestUri({
            url: `projects/${projectId}/${resourceMapper[resource]}`,
            params: { signed: token },
          }),
          "_blank",
          "noreferrer"
        )
      )
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      {isLoading ? (
        <CustomizationSpinnerContainer top="100px">
          <Spinner />
        </CustomizationSpinnerContainer>
      ) : (
        Object.keys(order).length > 0 && (
          <div className="order-summary">
            <div
              className="d-flex justify-content-between align-items-center border-bottom px-3"
              style={{ paddingBlock: "10px" }}
            >
              <div className="d-flex align-items-center column-gap-2">
                <p className="fw-bold fs-6 m-0">Order</p>
                {order.identifier&&<span className="text-muted small">#{order.identifier}</span>}
                {order.modules_count && (
                  <p className="m-0 grey_letter">
                    ({`${order.modules_count} modules`})
                  </p>
                )}
                {order.status && (
                  <Pill className="text-capitalize">{order.status}</Pill>
                )}
              </div>
              <i
                onClick={() => setCurrentTab("info")}
                className="fa-solid fa-xmark fs-4 pointer text-secondary"
              />
            </div>

            <div className="d-flex justify-content-between align-items-center px-2 border-bottom">
              <div className="d-flex">
                {access.view_price_detail && (
                  <p
                    className={`m-0 p-2 tab me-2 ${
                      activeTab === "upgrades" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("upgrades")}
                    style={{
                      cursor: "pointer",
                      fontWeight: "normal",
                      fontSize: "18px",
                      color: activeTab === "upgrades" ? "#2c92e6" : undefined,
                      borderBottom:
                        activeTab === "upgrades"
                          ? "2px solid #2c92e6"
                          : undefined,
                    }}
                  >
                    Summary view
                  </p>
                )}
                <p
                  className={`m-0 p-2 tab ${
                    activeTab === "room" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("room")}
                  style={{
                    cursor: "pointer",
                    fontWeight: "normal",
                    fontSize: "18px",
                    color: activeTab === "room" ? "#2c92e6" : undefined,
                    borderBottom:
                      activeTab === "room" ? "2px solid #2c92e6" : undefined,
                  }}
                >
                  Detailed view
                </p>
              </div>
              {order.price_access && (
                <UncontrolledDropdown>
                  <DropdownToggle className="border-0 order-download pointer">
                    <i className="fa-solid fa-download fs-6 pointer text-secondary" />
                  </DropdownToggle>
                  <DropdownMenu className="py-1">
                    <DropdownItem
                      onClick={() => downloadOrderInfo("order_summary")}
                    >
                      PO View
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        downloadOrderInfo("order_detailed_summary")
                      }
                    >
                      Detailed View
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </div>

            <div
              className="p-3"
              style={{
                overflowY: "auto",
                maxHeight: "calc(100vh - 300px)",
                height: "100%",
              }}
            >
              {activeTab === "room" ? (
                <UncontrolledAccordion stayOpen>
                  {order.rooms.map((room, index) => (
                    <AccordionItem key={index}>
                      <AccordionHeader targetId={room.id}>
                        <div className="d-flex flex-row-reverse">
                          <p className="m-0 ms-2 mt-1 grey_letter">
                            {`(${room.modules_count} modules)`}
                          </p>
                          <p className="m-0 fs-14px">{room.name}</p>
                        </div>
                        <div className="position-absolute end-0">
                          <p className="m-0 active-dimension">
                            {room.price &&
                              `₹${room.price.toLocaleString("en-IN")}`}
                          </p>
                        </div>
                      </AccordionHeader>
                      <AccordionBody accordionId={room.id}>
                        {room.groups.map((group, index) => (
                          <AccordionItem key={index}>
                            <AccordionHeader
                              targetId={room.id + "_" + group.id}
                            >
                              <div className="d-flex flex-row-reverse">
                                <p className="m-0 ms-2 mt-1 grey_letter">
                                  {`(${group.sqft} sq.ft)`}
                                </p>
                                <p className="m-0 fs-14px">{group.name}</p>
                              </div>
                              <div className="position-absolute end-0">
                                <p className="m-0">
                                  {group.price &&
                                    `₹${group.price.toLocaleString("en-IN")}`}
                                </p>
                              </div>
                            </AccordionHeader>
                            <AccordionBody
                              accordionId={room.id + "_" + group.id}
                            >
                              {group.modules.map((module, index) => (
                                <div
                                  key={index}
                                  className="d-flex justify-content-between"
                                >
                                  <p className="m-0 fs-14px">{module.name}</p>
                                  <div className="d-flex align-items-center column-gap-2">
                                    {module.is_discounted && (
                                      <p className="m-0 grey_letter text-decoration-line-through">
                                        {module.original_price &&
                                          `₹${module.original_price.toLocaleString(
                                            "en-IN"
                                          )}`}
                                      </p>
                                    )}
                                    <p className="m-0 text-secondary">
                                      {module.price &&
                                        `₹${module.price.toLocaleString(
                                          "en-IN"
                                        )}`}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </AccordionBody>
                          </AccordionItem>
                        ))}

                        {room.modules.map((module, index) => (
                          <div
                            key={index}
                            className="d-flex justify-content-between"
                          >
                            <p className="m-0 fs-14px">{module.name}</p>
                            <div className="d-flex align-items-center column-gap-2">
                              {module.is_discounted && (
                                <p className="m-0 grey_letter text-decoration-line-through">
                                  {module.original_price &&
                                    `₹${module.original_price.toLocaleString(
                                      "en-IN"
                                    )}`}
                                </p>
                              )}
                              <p className="m-0">
                                {module.price &&
                                  `₹${module.price.toLocaleString("en-IN")}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </AccordionBody>
                    </AccordionItem>
                  ))}
                  {addOns.length > 0 && (
                    <AccordionItem key="addOns">
                      <AccordionHeader targetId="addOns">
                        <div className="d-flex flex-row-reverse">
                          <p className="m-0 fs-14px">Add-Ons</p>
                        </div>
                        <div className="position-absolute end-0">
                          <p className="m-0 active-dimension">
                            ₹
                            {addOns
                              .reduce(
                                (sum, addOn) => sum + Number(addOn.price || 0),
                                0
                              )
                              .toLocaleString("en-IN")}{" "}
                          </p>
                        </div>
                      </AccordionHeader>
                      <AccordionBody accordionId="addOns">
                        {addOns.map((addOn) => (
                          <div
                            key={addOn.id}
                            className="d-flex justify-content-between"
                          >
                            <p className="m-0 fs-14px">{addOn.item}</p>
                            <div className="d-flex align-items-center column-gap-2">
                              <p className="m-0 text-secondary">
                                {`₹${addOn.price.toLocaleString("en-IN")}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </AccordionBody>
                    </AccordionItem>
                  )}
                  {(materialItems.length > 0 || (isAdmin && materialItems.length === 0)) && (
                    <AccordionItem key="materialItems">
                      <AccordionHeader targetId="materialItems">
                        <div className="d-flex flex-row-reverse align-items-center">
                          <p className="m-0 fs-14px">Additional Materials</p>
                        </div>
                        <div className="position-absolute end-0">
                          <p className="m-0 active-dimension">
                            ₹
                            {(
                              projectSummary?.price_summary?.total_material_items || 0
                            ).toLocaleString("en-IN")}{" "}
                          </p>
                        </div>
                      </AccordionHeader>
                      <AccordionBody accordionId="materialItems">
                        {materialItems.length > 0 ? (
                          materialItems.map((item) => (
                            <div
                              key={item.id}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <div>
                                <p className="m-0 fs-14px">
                                  {item.material?.name || "Material"}
                                </p>
                                {item.quantity > 0 && (
                                  <p className="m-0 grey_letter" style={{ fontSize: "12px" }}>
                                    {item.quantity}{" "}
                                    {item.material?.unit || ""} × ₹
                                    {Number(item.price || 0).toLocaleString("en-IN")}
                                  </p>
                                )}
                              </div>
                              <div className="d-flex align-items-center column-gap-2">
                                <p className="m-0 text-secondary">
                                  {`₹${(item.total_price || 0).toLocaleString("en-IN")}`}
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-2 text-muted">
                            No material items
                          </div>
                        )}
                      </AccordionBody>
                    </AccordionItem>
                  )}
                </UncontrolledAccordion>
              ) : (
                <div>
                  {projectSummary?.price_summary ? (
                    <>
                      {/* Total Value */}
                      <div className="mb-1">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <p className="m-0 fw-bold">Total Price</p>
                          <p
                            className="m-0 active-dimension text-end fw-bold"
                            style={{ width: "120px", flexShrink: 0 }}
                          >
                            ₹
                            {(
                              (projectSummary.price_summary.base_price || 0) +
                              (projectSummary.price_summary.total_upgrades ||
                                0) +
                              (projectSummary.price_summary.total_addons || 0) +
                              (projectSummary.price_summary.total_material_items || 0)
                            ).toLocaleString("en-IN")}
                          </p>
                        </div>
                      </div>
                      <div className="mb-1">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <p className="m-0 fw-bold">Base Price</p>
                          <p
                            className="m-0 active-dimension text-end fw-bold"
                            style={{ width: "120px", flexShrink: 0 }}
                          >
                            ₹
                            {(
                              projectSummary.price_summary.base_price || 0
                            ).toLocaleString("en-IN")}
                          </p>
                        </div>
                      </div>
                      <div className="mb-1">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <p className="m-0 fw-bold">Upgrades</p>
                          <p
                            className="m-0 active-dimension text-end fw-bold"
                            style={{ width: "120px", flexShrink: 0 }}
                          >
                            ₹
                            {(
                              projectSummary.price_summary.total_upgrades || 0
                            ).toLocaleString("en-IN")}
                          </p>
                        </div>
                      </div>
                      {projectSummary.price_summary.upgrades && (
                        <div>
                          <UncontrolledAccordion stayOpen>
                            {Object.entries(
                              projectSummary.price_summary.upgrades
                            )
                              .filter(([key, value]) => {
                                // Handle both old format (number) and new format (object with total)
                                const total =
                                  typeof value === "object" && value !== null
                                    ? value.total
                                    : value;
                                return total !== 0 && total !== undefined;
                              })
                              .map(([key, value]) => {
                                // Handle both old format (number) and new format (object with total and breakdown)
                                const total =
                                  typeof value === "object" && value !== null
                                    ? value.total
                                    : value;
                                const breakdown =
                                  typeof value === "object" && value !== null
                                    ? value.breakdown
                                    : null;
                                const accordionId = `upgrade-${key}`;

                                return (
                                  <AccordionItem key={key}>
                                    <AccordionHeader targetId={accordionId}>
                                      <div className="d-flex justify-content-between align-items-center w-100">
                                        <p
                                          className="m-0 text-capitalize ms-2"
                                          style={{
                                            fontSize: "14px",
                                            color: "#000",
                                          }}
                                        >
                                          {key
                                            .split("_")
                                            .map(
                                              (word) =>
                                                word.charAt(0).toUpperCase() +
                                                word.slice(1)
                                            )
                                            .join(" ")}
                                        </p>
                                        <p
                                          className="m-0 text-end"
                                          style={{
                                            fontSize: "14px",
                                            color: "#6c757d",
                                            width: "120px",
                                            flexShrink: 0,
                                          }}
                                        >
                                          ₹
                                          {Number(total).toLocaleString(
                                            "en-IN"
                                          )}
                                        </p>
                                      </div>
                                    </AccordionHeader>
                                    {breakdown && breakdown.length > 0 && (
                                      <AccordionBody accordionId={accordionId}>
                                        {breakdown
                                          .filter((item) => item.price !== 0)
                                          .map((item, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                            >
                                              <p
                                                className="m-0 ms-3"
                                                style={{
                                                  fontSize: "13px",
                                                  color: "#6c757d",
                                                }}
                                              >
                                                {item.name}
                                              </p>
                                              <p
                                                className="m-0 text-end"
                                                style={{
                                                  fontSize: "13px",
                                                  color: "#6c757d",
                                                  width: "120px",
                                                  flexShrink: 0,
                                                }}
                                              >
                                                ₹
                                                {Number(
                                                  item.price || 0
                                                ).toLocaleString("en-IN")}
                                              </p>
                                            </div>
                                          ))}
                                      </AccordionBody>
                                    )}
                                  </AccordionItem>
                                );
                              })}
                          </UncontrolledAccordion>
                          {Object.entries(
                            projectSummary.price_summary.upgrades
                          ).every(([key, value]) => {
                            const total =
                              typeof value === "object" && value !== null
                                ? value.total
                                : value;
                            return total === 0 || total === undefined;
                          }) && (
                            <div className="text-center py-4 text-muted">
                              No upgrades
                            </div>
                          )}
                        </div>
                      )}
                      {projectSummary.price_summary.addons &&
                        projectSummary.price_summary.addons.filter(
                          (addon) =>
                            addon.price !== 0 && addon.price !== undefined
                        ).length > 0 && (
                          <>
                            <div className="mb-1">
                              <div className="d-flex justify-content-between align-items-center py-1">
                                <p className="m-0 fw-bold">Addons</p>
                                <p
                                  className="m-0 active-dimension text-end"
                                  style={{ width: "120px", flexShrink: 0 }}
                                >
                                  ₹
                                  {(
                                    projectSummary.price_summary.total_addons ||
                                    0
                                  ).toLocaleString("en-IN")}
                                </p>
                              </div>
                            </div>
                            {access.view_price_detail && (
                              <div>
                                {projectSummary.price_summary.addons
                                  .filter(
                                    (addon) =>
                                      addon.price !== 0 &&
                                      addon.price !== undefined
                                  )
                                  .map((addon, index) => (
                                    <div
                                      key={index}
                                      className="d-flex justify-content-between py-1"
                                    >
                                      <p className="m-0 small ms-2">
                                        {addon.item}
                                      </p>
                                      <p
                                        className="m-0 small text-secondary text-end"
                                        style={{
                                          width: "120px",
                                          flexShrink: 0,
                                        }}
                                      >
                                        ₹
                                        {Number(
                                          addon.price || 0
                                        ).toLocaleString("en-IN")}
                                      </p>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </>
                        )}
                      {(materialItems.length > 0 || (isAdmin && materialItems.length === 0)) && (
                        <>
                          <div className="mb-1">
                            <div className="d-flex justify-content-between align-items-center py-1">
                              <div className="d-flex align-items-center">
                                <p className="m-0 fw-bold">Additional Materials</p>
                                {isAdmin && (
                                  <i
                                    className="fa-solid fa-edit fs-6 ms-2 pointer text-primary"
                                    onClick={() => {
                                      setCurrentTab("manage_material_items");
                                      loadMaterials();
                                    }}
                                  />
                                )}
                              </div>
                              <p
                                className="m-0 active-dimension text-end"
                                style={{ width: "120px", flexShrink: 0 }}
                              >
                                ₹
                                {(
                                  projectSummary?.price_summary?.total_material_items || 0
                                ).toLocaleString("en-IN")}
                              </p>
                            </div>
                          </div>
                          {access.view_price_detail && (
                            <div>
                              {materialItems.length > 0 ? (
                                materialItems.map((item, index) => (
                                  <div
                                    key={item.id || index}
                                    className="d-flex justify-content-between align-items-center py-1"
                                  >
                                    <div className="m-0 small ms-2">
                                      <p className="m-0">
                                        {item.material?.name || "Material"}
                                      </p>
                                      {item.quantity > 0 && (
                                        <p className="m-0 text-muted" style={{ fontSize: "11px" }}>
                                          {item.quantity}{" "}
                                          {item.material?.unit || ""} × ₹
                                          {Number(item.price || 0).toLocaleString("en-IN")}
                                        </p>
                                      )}
                                    </div>
                                    <div className="d-flex align-items-center column-gap-2">
                                      <p
                                        className="m-0 small text-secondary text-end"
                                        style={{
                                          width: "120px",
                                          flexShrink: 0,
                                        }}
                                      >
                                        ₹
                                        {Number(item.total_price || 0).toLocaleString("en-IN")}
                                      </p>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="text-center py-2 text-muted small ms-2">
                                  No material items
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      No upgrade data available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )
      )}

      <Modal isOpen={addEditModalOpen} toggle={() => toggleAddEditModal()}>
        <ModalHeader toggle={() => toggleAddEditModal()}>
          {currentMaterialItem.id ? "Edit Material Item" : "Add Material Item"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="material">Material</Label>
            <Input
              id="material"
              type="select"
              value={currentMaterialItem.material_id}
              onChange={(e) =>
                setCurrentMaterialItem({
                  ...currentMaterialItem,
                  material_id: e.target.value,
                })
              }
              invalid={!currentMaterialItem.material_id}
            >
              <option value="">Select Material</option>
              {materials && materials.length > 0 ? (
                materials.map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.name}
                    {material.identifier ? ` (${material.identifier})` : ""}
                    {material.brand ? ` - ${material.brand}` : ""}
                  </option>
                ))
              ) : (
                <option value="" disabled>No materials available</option>
              )}
            </Input>
            {!currentMaterialItem.material_id && (
              <FormFeedback>Please select a material.</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="price">Price per Unit</Label>
            <Input
              id="price"
              type="number"
              min="0"
              step="0.01"
              value={currentMaterialItem.price}
              onChange={(e) =>
                setCurrentMaterialItem({
                  ...currentMaterialItem,
                  price: e.target.value,
                })
              }
              invalid={
                currentMaterialItem.price === "" ||
                isNaN(currentMaterialItem.price) ||
                parseFloat(currentMaterialItem.price) < 0
              }
            />
            {(currentMaterialItem.price === "" ||
              isNaN(currentMaterialItem.price) ||
              parseFloat(currentMaterialItem.price) < 0) && (
              <FormFeedback>Price must be a valid number &gt;= 0.</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="quantity">Quantity</Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              step="1"
              value={currentMaterialItem.quantity}
              onChange={(e) =>
                setCurrentMaterialItem({
                  ...currentMaterialItem,
                  quantity: e.target.value,
                })
              }
              invalid={
                currentMaterialItem.quantity === "" ||
                isNaN(currentMaterialItem.quantity) ||
                parseInt(currentMaterialItem.quantity) <= 0
              }
            />
            {(currentMaterialItem.quantity === "" ||
              isNaN(currentMaterialItem.quantity) ||
              parseInt(currentMaterialItem.quantity) <= 0) && (
              <FormFeedback>Quantity must be a valid number &gt; 0.</FormFeedback>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleSaveMaterialItem}
            disabled={
              !currentMaterialItem.material_id ||
              currentMaterialItem.price === "" ||
              isNaN(currentMaterialItem.price) ||
              parseFloat(currentMaterialItem.price) < 0 ||
              currentMaterialItem.quantity === "" ||
              isNaN(currentMaterialItem.quantity) ||
              parseInt(currentMaterialItem.quantity) <= 0
            }
          >
            {currentMaterialItem.id ? "Update" : "Add"}
          </Button>
          <Button color="secondary" onClick={() => toggleAddEditModal()}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationModal
        isOpen={deleteConfirmationOpen}
        toggle={() => toggleDeleteConfirmation(null)}
        onConfirm={handleDeleteMaterialItem}
        message="Are you sure you want to delete this material item?"
        headingText="Confirm Deletion"
      />
    </>
  );
};

export default NewOrder;
